import { useRouter } from "next/router"
import { useSearchParams } from "next/navigation"
import { useFormContext } from "react-hook-form"
import { createOrder } from "@/lib/api/checkoutForm"
import postToSuperOffice from "@/lib/api/superOffice"
import { trackCheckoutForm } from "@/lib/helpers/tagManager"
import { useFormData, useStepByName } from "@/components/checkout-form/store"
import { useCartContext } from "@/components/woocommerce/cartStore"
import useCheckoutContext from "@/components/checkout-form/store"
import ReCAPTCHA from "react-google-recaptcha"
import { useRef } from "react"
import { usePropsObject } from "@/lib/contexts/appContext"
import { pushDataLayer } from "@/lib/helpers/tagManager"
import trackFinalStep from "@/components/checkout-form/payment/trackFinalStep"
import { addOrderLog } from "@/lib/api/checkoutForm"
import useBrowser from "@/lib/hooks/useBrowser"

export function PaymentForm({ children, orderSessionId }) {
  const { formState, setOrderResult, handleSubmit } = useFormContext()
  const { nonce, fetchCheckout } = useCartContext()
  const { thankYouPage, steps, type, trackingItem } = useCheckoutContext()
  const formData = useFormData()
  const router = useRouter()
  const recaptchaRef = useRef()
  const searchParams = useSearchParams()
  const browser = useBrowser()
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY

  const isGiftShop = useStepByName("giftShopCheckout") ?? false
  const { tracking } = usePropsObject()
  let orderIsComplete = false

  const onSubmit = async (values) => {
    if (formState.isSubmitting) return

    if (isGiftShop && nonce) {
      try {
        formData.giftShopCheckout = await fetchCheckout()
      } catch {
        return
      }
    }
    // Set to null before to empty the swedbankPayContainer and remove scripts that possibly has already been added.
    setOrderResult(null)

    if (orderIsComplete) {
      return
    }

    orderIsComplete = true

    let result
    const token = await recaptchaRef.current.executeAsync()
    await addOrderLog(orderSessionId, "Betalmetod", values.paymentMethod)
    await addOrderLog(
      orderSessionId,
      "Försöker skapa order med betalmetoden " + values.paymentMethod,
      "init"
    )

    switch (values.paymentMethod) {
      case "superoffice":
        result = await postToSuperOffice({
          ...formData,
          token: token,
          sourceUrl: document.location.origin + document.location.pathname
        })

        trackFinalStep(steps.length + 1, type, trackingItem, orderSessionId)
        trackCheckoutForm(result?.response?.tracking)
        await addOrderLog(orderSessionId, "Order skapad", result)

        // Redirect after 4 seconds
        setTimeout(function () {
          router.push(thankYouPage)
        }, 4000)

        break
      default:
        result = await createOrder({
          ...formData,
          paymentMethod: values.paymentMethod,
          sourceUrl: document.location.origin + document.location.pathname,
          orderSessionId,
          captchaToken: token,
          searchParams: Object.fromEntries(searchParams),
          inApp: browser.inApp ?? false,
          userAgent: browser?.ua?.source + ""
        })

        if (result?.redirect) {
          pushDataLayer({
            event: "payment_method",
            formType: tracking.formType,
            payment_method: values.paymentMethod
          })

          trackFinalStep(steps.length + 1, type, trackingItem, orderSessionId)
          trackCheckoutForm(result?.tracking)
          await addOrderLog(orderSessionId, "Order skapad", result)

          // Redirect after 4 seconds
          setTimeout(function () {
            router.push(result.redirect)
          }, 4000)

          return
        }

        setOrderResult(result)
        break
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ReCAPTCHA
        className="hidden"
        ref={recaptchaRef}
        size="invisible"
        sitekey={siteKey}
      />
      {children}
    </form>
  )
}
